// @ts-strict
// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## KeySignature.js
//
// A key signature object with routines for calculating key signatures.
import type { ClefName } from 'shared/definitions/musicTheory/clefs';
import type { KeyQuality } from 'shared/definitions/musicTheory/keySignatures';
import {
  keySignatureClefNotes,
  sharpMajors,
  flatMajors,
  sharpMinors,
  flatMinors,
  keySignatureFlats,
  keySignatureSharps,
} from 'shared/definitions/musicTheory/keySignatures';
import type { IntervalName } from 'shared/definitions/musicTheory/intervals';
import type { Accidental, NoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import { isNoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import type { AriaStringOptions } from './note';
import Note from './note';
import Scale from './scale';
import Chord from './chord';
import utils from '../utils';
import Clef from './clef';
import type Interval from './interval';
import rejectDuplicates from '../uTheory.Skills/rejectDuplicates';
import type { ScaleDegree } from '../uTheory.Interaction/solfegeDefinitions';

export type ScaleObject = Record<ScaleDegree, Note>;

class KeySignature {
  keyNote: Note;

  quality: 'Major' | 'minor';

  accidentalsCount: number;

  scale: Scale;

  relativeMajor: KeySignature;

  collection: ScaleObject;

  color?: string;

  accidentalColors?: string[];

  /**
   * Creates a new Key Signature object
   */
  // Public constructor
  constructor();
  constructor(keySignatureName: string);
  constructor(tonic: Note, quality?: string);
  constructor(tonic: string, quality?: string);
  constructor(accidentalCount: number, quality?: string);
  constructor(numberOfAccidentals: number | Note | string, quality?: string);
  constructor(
    noteNoteStringOrAccidentalCount?: string | Note | number,
    unparsedQualityString?: string
  ) {
    const { quality, keyNote, accidentalsCount, scale } = parseKey(
      noteNoteStringOrAccidentalCount,
      unparsedQualityString
    );
    this.quality = quality;
    this.keyNote = keyNote;
    this.accidentalsCount = accidentalsCount;
    this.scale = scale;
    this.relativeMajor = KeySignature.getRelativeMajor(this);
    this.collection = this.relativeMajor.getScale();
  }

  /**
   * Set the key signature using a tonic or number of accidentals and a quality
   */
  set(): KeySignature;
  set(keySignatureName: string): KeySignature;
  set(tonic: Note, quality?: string): KeySignature;
  set(tonic: string, quality?: string): KeySignature;
  set(numberOfAccidentals: number | string | Note, quality?: string): KeySignature;
  set(
    noteNoteStringOrAccidentalCount?: string | Note | number,
    unparsedQualityString?: string
  ): KeySignature {
    const { quality, keyNote, accidentalsCount, scale } = parseKey(
      noteNoteStringOrAccidentalCount,
      unparsedQualityString
    );
    this.quality = quality;
    this.keyNote = keyNote;
    this.accidentalsCount = accidentalsCount;
    this.scale = scale;
    this.relativeMajor = KeySignature.getRelativeMajor(this);
    this.collection = this.relativeMajor.getScale();
    return this;
  }

  transpose(interval: Interval | IntervalName) {
    const newKeyNote = this.keyNote.getNoteAt(interval);
    if (!newKeyNote) {
      throw new Error(`Cannot transpose the key signature ${this} by ${interval}`);
    }
    this.set(newKeyNote, this.quality);
  }

  /**
   * Get number of accidentals of this scale
   */
  getAccidentalsCount(noteName?: Note | string, quality?: 'Major' | 'minor'): number;
  getAccidentalsCount(keyNote = this.keyNote, quality = this.quality) {
    if (!(keyNote instanceof Note)) {
      keyNote = new Note(keyNote);
    }

    if (quality === 'Major') {
      return getMajorAccidentalsCount(keyNote);
    }
    if (quality === 'minor') {
      return getMinorAccidentalsCount(keyNote);
    }
    throw new Error(`KeySig: The quality must be Major or minor, not ${quality}.`);
  }

  /**
   * Returns the scale degree of the note in this key, if it exists. False otherwise.
   */
  getScaleDegreeAtNote(note: Note): ScaleDegree | false {
    const scaleObject = this.getScale();
    const scaleObjectEntry = Object.entries(scaleObject).find(([, degreeNote]) => {
      if (degreeNote.equalsIgnoreOctave(note)) return true;
      return false;
    });
    if (!scaleObjectEntry) return false;
    const matchedDegree = scaleObjectEntry[0] as keyof ScaleObject;
    return matchedDegree.replace('#', 'M') as ScaleDegree;
  }

  getNoteAtScaleDegreeOrThrow(scaleDegree: number | string, useHarmonicMinor = false): Note {
    const note = this.getNoteAtScaleDegree(scaleDegree, useHarmonicMinor);
    if (!note) {
      throw Error(`${scaleDegree} is not a valid degree in key ${this}`);
    }
    return note;
  }

  getNoteAtScaleDegree(scaleDegree: number | string, useHarmonicMinor = false): Note | false {
    if (scaleDegree === 'M6') scaleDegree = this.quality === 'minor' ? '#6' : '6';
    if (scaleDegree === 'M7') scaleDegree = this.quality === 'minor' ? '#7' : '7';

    // If useHarmonicMinor is set, we assume a raised leading tone
    // in minor, unless otherwise specified:
    if (
      useHarmonicMinor &&
      this.quality === 'minor' &&
      (scaleDegree === 7 || scaleDegree === '7')
    ) {
      scaleDegree = '#7';
    }
    if (this.quality === 'minor' && scaleDegree === 'b7') {
      scaleDegree = 7;
    }

    return this.scale.getNoteAtScaleDegree(`${scaleDegree}`);
  }

  /**
   * Gets the note at every scale degree
   */
  getScale(): ScaleObject {
    const sd1 = this.getNoteAtScaleDegree(1);
    const sd2 = this.getNoteAtScaleDegree(2);
    const sd3 = this.getNoteAtScaleDegree(3);
    const sd4 = this.getNoteAtScaleDegree(4);
    const sd5 = this.getNoteAtScaleDegree(5);
    const sd6 = this.getNoteAtScaleDegree(6);
    const sdM6 = this.getNoteAtScaleDegree('M6');
    const sd7 = this.getNoteAtScaleDegree(7);
    const sdM7 = this.getNoteAtScaleDegree('M7');
    const sd8 = this.getNoteAtScaleDegree(8);

    if (!sd1 || !sd2 || !sd3 || !sd4 || !sd5 || !sd6 || !sdM6 || !sd7 || !sdM7 || !sd8) {
      throw Error(`unable to get scale for key ${this}`);
    }

    return {
      '1': sd1,
      '2': sd2,
      '3': sd3,
      '4': sd4,
      '5': sd5,
      '6': sd6,
      M6: sdM6,
      '7': sd7,
      M7: sdM7,
      '8': sd8,
    };
  }

  /**
   * Returns the notes of the key signature (i.e. Bb4-Eb5-Ab4)
   * @param clef
   */
  getKeyNotesForClef(clef: ClefName | Clef): Note[] {
    if (this.accidentalsCount === 0) return [];
    const accidentalName = this.accidentalsCount > 1 ? 'sharps' : 'flats';
    const clefName = Clef.fromStringOrClef(clef).toString();
    if (clefName === 'percussion') {
      throw new Error('Percussion clefs cannot display key signatures.');
    }
    const keyNotes = keySignatureClefNotes[accidentalName][clefName];
    return keyNotes.slice(0, Math.abs(this.accidentalsCount)).map(Note.fromStringOrNote);
  }

  /**
   * Checks if this key contains a certain note
   */
  contains(note: Note) {
    return !!Object.values(this.collection).find((scaleNote) => scaleNote.equalsIgnoreOctave(note));
  }

  getAccidentalAtNote(noteOrNoteName: Note | NoteName): Accidental {
    const note = Note.stringToNote(noteOrNoteName);
    const matchedScaleObjectEntry = Object.entries(this.collection).find(
      ([scaleDegree, scaleNote]) => {
        // We don't want the harmonic/melodic minor version, we want the one in the key sig:
        if (scaleDegree.includes('M') || scaleDegree.includes('#')) return false;
        return scaleNote.letter === note.letter;
      }
    );
    if (!matchedScaleObjectEntry) {
      // this should never happen, because scales have all 7 notes...
      throw Error(`Couldn't get accidental for note ${noteOrNoteName} in key ${this}`);
    }
    return matchedScaleObjectEntry[1].accidental;
  }

  /**
   * Change the accidental of the note to match the key signature. Caution: Modifies
   * the note passed as an argument directly, DOES NOT clone it.
   */
  applyKeySignatureToNote(note: Note | NoteName): Note {
    note = note instanceof Note ? note : Note.stringToNote(note);
    const accidental = this.getAccidentalAtNote(note);
    note.accidental = accidental;
    return note;
  }

  getRelativeKey() {
    if (this.quality === 'Major') return this.getRelativeMinor();
    if (this.quality === 'minor') return this.getRelativeMajor();
    throw Error(`Quality must be Major or minor, not ${this.quality} to get relative key.`);
  }

  static getRelativeMajor(key: KeySignature) {
    if (key.quality === 'Major') return key;
    let count = key.getAccidentalsCount();
    const sharp = count > 0;
    let majorName: NoteName;
    if (sharp) {
      majorName = sharpMajors[count]!;
    } else {
      count = Math.abs(count);
      majorName = flatMajors[count]!;
    }
    return new KeySignature(majorName, 'Major');
  }

  getRelativeMajor() {
    return KeySignature.getRelativeMajor(this);
  }

  static getRelativeMinor(key: KeySignature) {
    if (key.quality === 'minor') return key;
    let count = key.getAccidentalsCount();
    const sharp = count > 0;
    let minorName: NoteName;
    if (sharp) {
      minorName = sharpMinors[count]!;
    } else {
      count = Math.abs(count);
      minorName = flatMinors[count]!;
    }
    return new KeySignature(minorName, 'minor');
  }

  getRelativeMinor() {
    return KeySignature.getRelativeMinor(this);
  }

  /**
   * Get parallel scale -- always valid, even when a parallel key doesn't exist
   * @returns {ScaleObject} parallel natural minor or Major scale
   */
  getParallelScale(): ScaleObject {
    if (this.quality === 'Major') {
      return transformScaleObject(this.getScale(), 'Major', 'minor');
    }
    return transformScaleObject(this.getScale(), 'minor', 'Major');
  }

  /**
   * Return the parallel key, if it exists.
   * # CAUTION: Throws if it doesn't exist!
   */
  getParallelKey() {
    const quality = this.quality === 'Major' ? 'minor' : 'Major';
    return new KeySignature(this.keyNote, quality);
  }

  clone() {
    return new KeySignature(this.keyNote.clone(), this.quality);
  }

  setTonic(tonic: Note | string): this;
  setTonic(numberOfAccidentals: number): this;
  setTonic(tonic: Note | string | number) {
    this.set(tonic, this.quality);
    return this;
  }

  setQuality(quality: string) {
    this.set(this.keyNote, quality);
    return this;
  }

  get tonic(): Note {
    return this.keyNote;
  }

  set tonic(note: Note) {
    this.set(note, this.quality);
  }

  /**
   * Get the tonic triad of the key
   */
  get tonicTriad() {
    return new Chord(this.tonic.clone(), this.quality);
  }

  /**
   * Get the notes with accidentals in this key
   */
  getKeyNotes() {
    if (this.accidentalsCount === 0) return [];
    return this.accidentalsCount > 0
      ? keySignatureSharps.slice(0, this.accidentalsCount)
      : keySignatureFlats.slice(0, this.accidentalsCount * -1);
  }

  equals(that: KeySignature | string) {
    that = KeySignature.stringToKey(that);
    return this.accidentalsCount === that.accidentalsCount && this.quality === that.quality;
  }

  equalsOrRelative(that: KeySignature | string) {
    that = KeySignature.stringToKey(that);
    return this.accidentalsCount === that.accidentalsCount;
  }

  equalsOrParallel(that: KeySignature | string) {
    that = KeySignature.stringToKey(that);
    return this.tonic.equalsIgnoreOctave(that.tonic);
  }

  toAriaString(ariaOptions: AriaStringOptions = {}): string {
    const { keySignature = true } = ariaOptions;
    if (!keySignature) return '';
    const accidentalsCount = keySignature === true || keySignature.accidentalsCount !== false;
    const accidentalsNames = keySignature === true || keySignature.accidentalsNames !== false;
    if (!accidentalsCount && !accidentalsNames) return '';

    const keyNotesString = accidentalsNames
      ? this.getKeyNotes()
          .map((str) => new Note(str))
          .map((note) => note.toAriaString({ octave: false }))
          .join(' ')
      : '';

    let accidentalName = this.accidentalsCount > 0 ? 'sharp' : 'flat';
    if (Math.abs(this.accidentalsCount) > 1) accidentalName += 's';

    const accidentalsCountString = accidentalsCount
      ? `${Math.abs(this.accidentalsCount)} ${accidentalName}`
      : '';

    const space = accidentalsCountString.length && keyNotesString.length ? ', ' : '';
    const ariaString = accidentalsCountString + space + keyNotesString;

    return this.accidentalsCount ? ariaString : 'no sharps or flats';
  }

  toString() {
    return `${this.keyNote.toShortString()} ${this.quality}`;
  }

  /**
   * Create a string of the key with slash between key note and quality
   */
  toMusicFileString() {
    return `${this.keyNote.toShortString()}/${this.quality}`;
  }

  /**
   * Create a string of the key with accidentals formated
   */
  toAccidentalFontString({ shortQuality = false } = {}) {
    return `${this.keyNote.toAccidentalFontShortString()} ${
      shortQuality ? this.quality.slice(0, 3) : this.quality
    }`;
  }

  toVexString() {
    const minor = this.quality === 'minor' ? 'm' : '';
    return this.keyNote.toShortString() + minor;
  }

  /**
   * Set the color of the KeySignature object
   */
  setColor(color: string) {
    this.color = color;
  }

  /**
   * Set a specified accidental a certain color
   */
  setAccidentalColor(n: number, color: string) {
    if (typeof this.accidentalColors === 'undefined') {
      this.accidentalColors = [];
    }
    this.accidentalColors[n] = color;
  }

  /**
   * Clear all KeySignature and accidental colors
   */
  clearColoring() {
    this.color = undefined;
    this.accidentalColors = undefined;
  }

  /**
   * Convert a string or KeySignature object into a KeySignature object
   */
  static stringToKey(key: string | KeySignature) {
    if (key instanceof KeySignature) return key;
    return new KeySignature(key);
  }

  /**
   * Get all keys, optionally of a specific quality
   * @param limitToQuality - if omitted, provides both Major and minor
   */
  static allKeys(limitToQuality?: 'Major' | 'minor') {
    const qualities = limitToQuality ? [limitToQuality] : ['Major', 'minor'];
    // eslint-disable-next-line no-shadow
    return qualities
      .map((includedQuality) => {
        const keys: KeySignature[] = [];
        for (let i = -7; i <= 7; i++) {
          keys.push(new KeySignature(i, includedQuality));
        }
        return keys;
      })
      .reduce((a, b) => a.concat(b));
  }

  /**
   * get a random key signature
   * @param {string} quality 'Major' or 'minor', falsey for both
   * @param {boolean} omitEnharmonics limit to 12 chromatic keys, useful for ear training
   */
  static randomKey(quality?: string, omitEnharmonics?: boolean): KeySignature {
    quality = quality || (Math.random() > 0.5 ? 'Major' : 'minor');
    const maxAcc = omitEnharmonics ? 12 : 15;
    const subAcc = omitEnharmonics ? 4 : 7;
    const acc = Math.floor(Math.random() * maxAcc) - subAcc;
    return new KeySignature(acc, quality);
  }

  /**
   * get several random key signatures
   * @param {number} numberOfKeys
   * @param {string} quality 'Major' 'minor', or omit for both
   * @param {boolean} [omitEnharmonics] restrict to just the 12 chromatic keys for each type,
   *  useful for ear training
   * @returns {KeySignature[]}
   */
  static randomKeys(
    numberOfKeys: number,
    quality?: string,
    omitEnharmonics?: boolean
  ): KeySignature[] {
    const flats = omitEnharmonics ? -4 : -7;
    const sharps = 7;
    const possibleKeys = [];
    for (let i = flats; i <= sharps; i++) {
      if (quality) {
        possibleKeys.push(new KeySignature(i, quality));
      } else {
        possibleKeys.push(new KeySignature(i, 'Major'));
        possibleKeys.push(new KeySignature(i, 'minor'));
      }
    }
    const equals = (a: KeySignature, b: KeySignature) => a.tonic.equalsIgnoreOctave(b.tonic);
    const adjacentEquality = (a: KeySignature, b: KeySignature) =>
      a.tonic.letter === b.tonic.letter;
    const keys = rejectDuplicates(possibleKeys, numberOfKeys, {
      equals,
      adjacentEquality,
    });
    return keys;
  }
}

function getMajorAccidentalsCount(keyNote: Note): number {
  let accidentalsCount;
  const keyWithSharps = sharpMajors.indexOf(keyNote.toShortString() as any);
  const keyWithFlats = flatMajors.indexOf(keyNote.toShortString() as any);

  if (keyWithSharps !== -1) {
    accidentalsCount = keyWithSharps;
  } else if (keyWithFlats !== -1) {
    accidentalsCount = -keyWithFlats;
  } else {
    throw new Error(`KeySig: ${keyNote} is not a valid Major key name.`);
  }

  return accidentalsCount;
}

function getMinorAccidentalsCount(keyNote: Note): number {
  let accidentalsCount;
  const keyWithSharps = sharpMinors.indexOf(keyNote.toShortString() as any);
  const keyWithFlats = flatMinors.indexOf(keyNote.toShortString() as any);

  if (keyWithSharps !== -1) {
    accidentalsCount = keyWithSharps;
  } else if (keyWithFlats !== -1) {
    accidentalsCount = -keyWithFlats;
  } else {
    throw new Error(`KeySig: ${keyNote} is not a valid minor key name.`);
  }

  return accidentalsCount;
}

export default KeySignature;

interface ParsedKeyInput {
  keyNote: Note;
  quality: KeyQuality;
  accidentalsCount: number;
  scale: Scale;
}

function parseKey(): ParsedKeyInput;
function parseKey(keySignatureName: string): ParsedKeyInput;
function parseKey(
  tonicTonicStringOrAccidentalCount?: Note | string | number,
  quality?: string
): ParsedKeyInput;
function parseKey(tonic: string, quality?: string): ParsedKeyInput;
function parseKey(numberOfAccidentals: number | string | Note, quality?: string): ParsedKeyInput;
function parseKey(
  noteNoteStringOrAccidentalCount?: string | Note | number,
  unparsedQualityString?: string
): ParsedKeyInput {
  // If given in shorthand (note/key) split into variables:
  if (
    typeof unparsedQualityString === 'undefined' &&
    utils.isString(noteNoteStringOrAccidentalCount)
  ) {
    // If there's a space or slash, we split on it
    if (noteNoteStringOrAccidentalCount.search(/[/\s]/) !== -1) {
      // it has a space or slash
      const split = noteNoteStringOrAccidentalCount.split(/[/\s]/);
      noteNoteStringOrAccidentalCount = split[0];
      unparsedQualityString = split[1];
    } else {
      // Maybe they put the quality IN the string itself:
      unparsedQualityString =
        noteNoteStringOrAccidentalCount.search('m') !== -1 ||
        noteNoteStringOrAccidentalCount.search('-') !== -1
          ? 'minor'
          : 'Major';
    }
  }

  const quality = parseKeyQualityInput(unparsedQualityString);
  const keyNote = parseKeyNoteInput(noteNoteStringOrAccidentalCount, quality);

  const accidentalsCount =
    quality === 'Major' ? getMajorAccidentalsCount(keyNote) : getMinorAccidentalsCount(keyNote);

  const scale = new Scale(keyNote, quality);
  return { quality, keyNote, accidentalsCount, scale };
}

function parseKeyNoteInput(
  noteNoteStringOrAccidentalCount: string | Note | number | undefined,
  quality: KeyQuality
): Note {
  // If none given, use default C
  if (typeof noteNoteStringOrAccidentalCount === 'undefined') return new Note();

  // if input is a Note, make it the keyNote.
  if (noteNoteStringOrAccidentalCount instanceof Note) {
    return noteNoteStringOrAccidentalCount;
  }

  // If it's a number, or numeric, it's an accidental count.
  if (
    typeof noteNoteStringOrAccidentalCount === 'number' ||
    utils.isNumeric(noteNoteStringOrAccidentalCount)
  ) {
    const accidentalCount = Number(noteNoteStringOrAccidentalCount);

    // Sharp keys:
    if (noteNoteStringOrAccidentalCount >= 0) {
      const noteName =
        quality === 'Major'
          ? sharpMajors[Math.abs(accidentalCount)]
          : sharpMinors[Math.abs(accidentalCount)];
      if (noteName) return new Note(noteName);
    }

    // Flat keys:
    const noteName =
      quality === 'Major'
        ? flatMajors[Math.abs(accidentalCount)]
        : flatMinors[Math.abs(accidentalCount)];

    if (!noteName) {
      throw Error(
        `${noteNoteStringOrAccidentalCount} is not a valid tonic/accidental number for a key signature.`
      );
    }

    return new Note(noteName);
  }

  // They may have left out a space, e.g., 'CM', so let's filter out any quality like things:
  const cleanedNoteName = noteNoteStringOrAccidentalCount.replace(/[M|m|a|j|o|r|i|n| ]/g, '');
  if (isNoteName(cleanedNoteName)) {
    return new Note(cleanedNoteName);
  }

  // otherwise, we throw!
  throw Error(
    `Invalid key note input: ${noteNoteStringOrAccidentalCount} must be of type string, number or undefined.`
  );
}

function parseKeyQualityInput(keyQualityString?: string): KeyQuality {
  // If none given, use default C Major
  if (typeof keyQualityString === 'undefined') return 'Major';

  // Set quality (must be Major or minor)
  const majorShorthands = ['Major', 'M', 'Maj', 'maj', 'major'];
  const minorShorthands = ['minor', 'm', 'min', 'Minor', 'Min'];

  if (majorShorthands.indexOf(keyQualityString) !== -1) {
    return 'Major';
  }
  if (minorShorthands.indexOf(keyQualityString) !== -1) {
    return 'minor';
  }
  throw new Error(
    `KeySig: Key signature quality must be either Major or minor, not ${keyQualityString}.`
  );
}

function transformScaleObject(
  scaleObject: ScaleObject,
  fromQuality: KeyQuality,
  toQuality: KeyQuality
): ScaleObject {
  if (fromQuality === toQuality) return scaleObject;
  const newScaleObject: ScaleObject = {
    ...scaleObject,
  };
  if (toQuality === 'Major') {
    ([3, 6, 7] as const).forEach((degree) => {
      const raisedNote = newScaleObject[degree].getNoteAt('A1');
      if (!raisedNote) throw Error(`Cannot get parallel major note for ${newScaleObject[degree]}`);
      newScaleObject[degree] = raisedNote;
    });
    return newScaleObject;
  }

  ([3, 6, 7] as const).forEach((degree) => {
    const raisedNote = newScaleObject[degree].getNoteAt('-A1');
    if (!raisedNote) throw Error(`Cannot get parallel minor note for ${newScaleObject[degree]}`);
    newScaleObject[degree] = raisedNote;
  });
  return newScaleObject;
}
